import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { SERVICES, PROMISES } from "../data/content";

export default function Services() {
    return (
        <section
            id="services"
            data-testid="services-section"
            className="relative bg-haas-cream py-24 sm:py-32"
        >
            <div className="max-w-7xl mx-auto px-5 sm:px-10">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{ duration: 0.6 }}
                    className="text-center max-w-3xl mx-auto mb-16"
                >
                    <span className="chip" data-testid="services-kicker">
                        🔨 What we do
                    </span>
                    <h2 className="mt-6 font-display font-bold text-4xl sm:text-6xl tracking-tight text-haas-ink leading-[1.05]">
                        Four services.{" "}
                        <span className="marker-highlight-coral">Done really well.</span>
                    </h2>
                    <p className="mt-5 text-haas-soft text-lg leading-relaxed">
                        We're landscaping specialists — artificial grass, patios, driveways and fencing. That's all we do, and we do it properly.
                    </p>
                </motion.div>

                {/* Service cards */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 mb-24">
                    {SERVICES.map((s, i) => (
                        <motion.div
                            key={s.id}
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.2 }}
                            transition={{ duration: 0.6, delay: i * 0.1 }}
                            data-testid={`service-card-${s.id}`}
                            className="hard-card p-8 sm:p-10 relative overflow-hidden"
                        >
                            <div className={`absolute -top-12 -right-12 w-56 h-56 rounded-full ${s.accent} opacity-70`} />

                            <div className="relative">
                                <div className="text-6xl mb-5">{s.emoji}</div>
                                <div className="font-hand text-haas-goldDeep text-3xl mb-1 -rotate-2 inline-block">
                                    {s.kicker}
                                </div>
                                <h3 className="font-display font-bold text-4xl sm:text-5xl text-haas-ink leading-none mt-2">
                                    {s.title}
                                </h3>
                                <p className="mt-6 text-haas-soft leading-relaxed text-base">
                                    {s.description}
                                </p>
                                <ul className="mt-7 space-y-3">
                                    {s.features.map((f) => (
                                        <li
                                            key={f}
                                            className="flex items-start gap-3 text-sm text-haas-ink font-medium"
                                        >
                                            <span className="flex-shrink-0 mt-0.5 w-5 h-5 rounded-full bg-haas-ink text-haas-cream flex items-center justify-center">
                                                <Check className="w-3 h-3" strokeWidth={3} />
                                            </span>
                                            <span>{f}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* Promises strip */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.7 }}
                    className="text-center max-w-2xl mx-auto mb-12"
                >
                    <h3 className="font-display font-bold text-3xl sm:text-4xl text-haas-ink leading-tight">
                        Our four little promises
                    </h3>
                    <p className="mt-3 text-haas-soft">
                        Nothing fancy. Just the basics — done every single time.
                    </p>
                </motion.div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {PROMISES.map((p, i) => (
                        <motion.div
                            key={p.title}
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.2 }}
                            transition={{ duration: 0.5, delay: i * 0.08 }}
                            data-testid={`promise-card-${i + 1}`}
                            className="bg-white border-2 border-haas-ink rounded-2xl p-6 hover:bg-haas-sand transition-colors"
                        >
                            <div className="text-3xl mb-3">{p.emoji}</div>
                            <div className="font-display font-bold text-lg text-haas-ink mb-2">
                                {p.title}
                            </div>
                            <p className="text-sm text-haas-soft leading-relaxed">
                                {p.text}
                            </p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}
